import React from 'react';
import { Play, Check, Utensils } from 'lucide-react';

// Groups every item across all visible KOTs by name + variant and sums quantities,
// so kitchen staff see "5 Chicken Tandoori" instead of hunting across 5 table cards.
function buildConsolidatedGroups(orders, itemStatusFilter, showReadyItems) {
  const groups = {};

  orders.forEach(order => {
    const items = order.items || order.orderItems || [];
    items.forEach(item => {
      const status = (item.itemStatus || 'PENDING').toUpperCase();

      if (status === 'SERVED') return; // KDS never shows served items

      if (itemStatusFilter === 'new' && status !== 'PENDING') return;
      if (itemStatusFilter === 'cooking' && status !== 'PREPARING') return;
      if (itemStatusFilter === 'ready' && status !== 'READY') return;
      // Note: unlike the table view, this doesn't replicate the 20s "ready" grace
      // timer from KDSOrderCard — it just respects the showReadyItems toggle directly.
      if (status === 'READY' && !showReadyItems && itemStatusFilter !== 'ready') return;

      const name = item.itemName || item.menuItemName || item.name || 'Item';
      const variant = item.variantName || item.variant || '';
      const key = `${name}::${variant}`;

      if (!groups[key]) {
        groups[key] = {
          key, name, variant,
          totalQty: 0, pendingQty: 0, preparingQty: 0, readyQty: 0,
          instances: [],
        };
      }

      const g = groups[key];
      const qty = item.quantity || 1;
      g.totalQty += qty;
      if (status === 'PENDING') g.pendingQty += qty;
      else if (status === 'PREPARING') g.preparingQty += qty;
      else if (status === 'READY') g.readyQty += qty;

      g.instances.push({
        orderId: order.id,
        itemId: item.id,
        quantity: qty,
        status,
        tableName: order.tableName || `Table ${order.tableId}`,
        tableSplit: order.tableSplit,
        kotNumber: order.kotNumber || order.id,
        specialInstructions: item.specialInstructions || item.notes,
      });
    });
  });

  // Pending items first, then preparing, then alphabetical
  return Object.values(groups).sort((a, b) => {
    if ((a.pendingQty > 0) !== (b.pendingQty > 0)) return a.pendingQty > 0 ? -1 : 1;
    if ((a.preparingQty > 0) !== (b.preparingQty > 0)) return a.preparingQty > 0 ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

export default function KDSConsolidatedView({
  orders, itemStatusFilter, showReadyItems, onUpdateItemStatus, gridClass, gapClass
}) {
  const groups = React.useMemo(
    () => buildConsolidatedGroups(orders, itemStatusFilter, showReadyItems),
    [orders, itemStatusFilter, showReadyItems]
  );

  if (groups.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Utensils size={48} className="mx-auto mb-3 text-emerald-500" />
          <h3 className="text-lg font-semibold text-slate-300 mb-1">Nothing to prep</h3>
          <p className="text-slate-500 text-sm">No items match this filter right now</p>
        </div>
      </div>
    );
  }

  // Bump every instance in this group that's currently in fromStatus
  const bumpGroup = (group, fromStatus, toStatus) => {
    group.instances
      .filter(i => i.status === fromStatus)
      .forEach(i => onUpdateItemStatus(i.orderId, i.itemId, toStatus));
  };

  return (
    <div className={`grid ${gridClass} ${gapClass}`}>
      {groups.map(group => (
        <ConsolidatedGroupCard
          key={group.key}
          group={group}
          onStart={() => bumpGroup(group, 'PENDING', 'PREPARING')}
          onReady={() => bumpGroup(group, 'PREPARING', 'READY')}
        />
      ))}
    </div>
  );
}

function ConsolidatedGroupCard({ group, onStart, onReady }) {
  const [expanded, setExpanded] = React.useState(false);
  const borderColor =
    group.pendingQty > 0 ? 'border-l-teal-500' :
    group.preparingQty > 0 ? 'border-l-amber-500' : 'border-l-emerald-500';

  return (
    <div className={`bg-slate-800 rounded-lg border-l-4 ${borderColor} overflow-hidden shadow-lg`}>
      <div className="px-3 py-2 bg-slate-700 flex items-center justify-between">
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-white truncate">{group.name}</h3>
          {group.variant && <p className="text-xs text-slate-400 truncate">{group.variant}</p>}
        </div>
        <span className="flex-shrink-0 ml-2 w-9 h-9 rounded-full bg-slate-900 text-white font-bold text-sm flex items-center justify-center">
          {group.totalQty}
        </span>
      </div>

      <div className="px-3 py-2 flex items-center gap-2 text-xs">
        {group.pendingQty > 0 && (
          <span className="px-2 py-0.5 rounded bg-slate-600 text-slate-200">{group.pendingQty} new</span>
        )}
        {group.preparingQty > 0 && (
          <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">{group.preparingQty} cooking</span>
        )}
        {group.readyQty > 0 && (
          <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">{group.readyQty} ready</span>
        )}
      </div>

      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full px-3 py-1 text-left text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-700/50"
      >
        {expanded ? 'Hide tables' : `From ${group.instances.length} order${group.instances.length > 1 ? 's' : ''}`}
      </button>

      {expanded && (
        <div className="px-3 pb-2 space-y-1">
          {group.instances.map((inst, idx) => (
            <div key={idx} className="flex items-center justify-between text-xs text-slate-400">
              <span>
                {inst.tableName}{inst.tableSplit && inst.tableSplit !== 'A' ? ` (${inst.tableSplit})` : ''} · KOT #{inst.kotNumber}
              </span>
              <span>x{inst.quantity}</span>
            </div>
          ))}
        </div>
      )}

      <div className="px-3 pb-3 flex gap-2">
        {group.pendingQty > 0 && (
          <button
            onClick={onStart}
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-teal-500 hover:bg-teal-400 text-white text-xs font-medium transition-colors"
          >
            <Play size={14} /> Start {group.pendingQty}
          </button>
        )}
        {group.preparingQty > 0 && (
          <button
            onClick={onReady}
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-medium transition-colors"
          >
            <Check size={14} /> Ready {group.preparingQty}
          </button>
        )}
      </div>
    </div>
  );
}